<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ProductEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    product: {
      type: Object,
      required: false,
    },
    target: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getPayload = (...args) => {
    const [data] = args;

    if (!props.target) {
      return data;
    }

    const fieldsToSubmit = targetConfig.value[props.target].fieldsToSubmit;
    if (fieldsToSubmit.indexOf('*') > -1) {
      return data;
    }

    return _.pick(data, fieldsToSubmit);
  };

  const formId = ref(null);
  const defaultTitle = ref(
    $fn.value.pgettext('Product', 'Edit General Information')
  );
  const targetConfig = ref({
    general: {
      title: $fn.value.pgettext('Product', 'Edit general data'),
      fieldsToSubmit: ['*'],
    },
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'md',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    defaultTitle: defaultTitle.value,
    targetConfig: targetConfig.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'defaultTitle' },
      { name: 'targetConfig' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId },
    ux: {},
    dataRefs: {
      formId,
      defaultTitle,
      targetConfig,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: props.target
          ? targetConfig.value[props.target].title
          : defaultTitle.value,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.product,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('product');
    }
  );
  watch(
    () => props.target,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('target');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_0x7qs75vp_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_qbs183xc7_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_imv7tmx2l_getActionArgs = async ($event) => {
    return {
      objectType: 'product',
      op: 'upsert',
      data: getPayload(props.product),
    };
  };

  const act_7315092382398562_closeModal_0x7qs75vp =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_0x7qs75vp_getActionArgs,
    });
  const inst_evh_7315092382398562_close_v2od2c1os =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_0x7qs75vp,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_v2od2c1os],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_oxygp84bk =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_gqot3px5s =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_oxygp84bk,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_gqot3px5s],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_41p7w30zs =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_7zvfsp611 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_41p7w30zs,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_7zvfsp611],
    event: 'close',
  });
  const act_1040167445267876_closeModal_qbs183xc7 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_qbs183xc7_getActionArgs,
    });
  const inst_evh_1040167445267876_success_3ixf4nfc5 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_qbs183xc7,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_imv7tmx2l = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_imv7tmx2l_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_3ixf4nfc5],
  });
  const inst_evh_1040167445267876_submit_e8t3fqswr =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_imv7tmx2l,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_e8t3fqswr],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_d3xoyh9zl =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_g1djxhl42 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_d3xoyh9zl,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_g1djxhl42],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_v2od2c1os,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_gqot3px5s,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_7zvfsp611,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_3ixf4nfc5,
    inst_evh_1040167445267876_submit_e8t3fqswr,
    save: inst_evh_1040167445267876_submit_e8t3fqswr,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_g1djxhl42,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_v2od2c1os = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_v2od2c1os.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_gqot3px5s = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_gqot3px5s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_7zvfsp611 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_7zvfsp611.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_3ixf4nfc5 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_3ixf4nfc5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_e8t3fqswr = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_e8t3fqswr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_g1djxhl42 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_g1djxhl42.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ProductEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="product"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <FieldsetProductGeneral
            :product="product"
            :formId="formId"
            :target="target"
          >
          </FieldsetProductGeneral>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms.product || !forms.product.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
